import React, { useState } from "react";
import { Grid, TextField, IconButton, InputLabel, Box } from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import Request from "../Utils/Request/Request";
import { showToast } from "../Utils";

const ProductExtraLabels = ({
  labels,
  setLabels,
  label,
  index,
  singleProduct,
  formData,
  setFormData,
  labelIndex,
  isFirst,
  showEdits
}) => {
  const [isEditing, setIsEditing] = useState(label.isNew || false);

  // Handle input change
  const handleChange = (e) => {
    const updatedLabels = [...labels];
    updatedLabels[labelIndex].valueLabel = e.target.value;
    setLabels(updatedLabels);
  };

  // Save label to API
  const saveLabel = async () => {
    if (!label.valueLabel.trim()) return;
    try {
      const response = await Request.post("/createExtraLabel", {
        valueLabel: label.valueLabel,
      });
      const updatedLabels = [...labels];
      updatedLabels[labelIndex] = response.data.data; // Replace with saved label from API
      setLabels(updatedLabels);
      setIsEditing(false);
      showToast("success", "Label created successfully!");
    } catch (error) {
      console.error("Error saving label", error?.response?.data?.message);
      showToast("error", error?.response?.data?.message);
    }
  };

  const updateLabel = async () => {
    if (!label.valueLabel.trim()) return;
    try {
      const response = await Request.put(`/updateExtraLabel/${label.id}`, {
        valueLabel: label.valueLabel,
      });
  
      const updatedLabels = [...labels];
      updatedLabels[labelIndex] = response.data.data;
      setLabels(updatedLabels);
      showToast("success", "Label updated successfully");
      setIsEditing(false);
  
      // Optional: Update formData labels if you're showing live labels
      const updatedFormData = { ...formData };
      const product = updatedFormData.invoiceproducts[index];
      if (
        product.invoiceProductExtraLabels &&
        product.invoiceProductExtraLabels[labelIndex]?.labelId === label.id
      ) {
        product.invoiceProductExtraLabels[labelIndex].label = label.valueLabel;
      }
      setFormData(updatedFormData);
    } catch (error) {
      console.error("Error updating label", error?.response?.data?.message);
      showToast("error", error?.response?.data?.message);
    }
  };

  
  const deleteLabel = async () => {
    if (!label.isNew) {
      try {
        await Request.put(`/deleteExtraLabel/${label.id}`); // changed to PUT
        showToast("success", "Label deleted.");
      } catch (error) {
        console.error("Error deleting label", error);
        showToast("error", "Failed to delete label.");
      }
    }
    const updatedLabels = labels.filter((_, i) => i !== labelIndex);
    setLabels(updatedLabels);
  };
  

  return (
    <Grid container direction="column" spacing={1}>
      {/* Label Input or Text */}
      <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {(isEditing && isFirst) ? (
          <TextField
            placeholder="Add Label"
            value={label.valueLabel}
            onChange={handleChange}
            size="small"
            sx={{
              "& .MuiInputLabel-shrink": {
                color: "#41c1ba !important",
              },
              "& fieldset": {
                borderColor: "#41c1ba !important",
                "& legend": {
                  "& span": {
                    color: "#41c1ba !important",
                  },
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#41c1ba",
              },
              width: "100%",
            }}
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InputLabel>{label.valueLabel || ""}</InputLabel>
              {(isFirst && showEdits === true) && (
                <>
                  <IconButton
                    onClick={() => setIsEditing(true)}
                    color="primary"
                  >
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "#41c1ba",
                        fontSize: "1rem",
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={deleteLabel} color="error">
                    <CloseIcon sx={{ cursor: "pointer", fontSize: "1rem" }} />
                  </IconButton>
                </>
              )}
            </Box>
            {!label.isNew &&
            <TextField
              size="small"
              value={
                formData?.invoiceproducts[index]?.invoiceProductExtraLabels?.[
                  labelIndex
                ]?.value || ""
              }
              onChange={(e) => {
                const updatedFormData = { ...formData };
                const product = updatedFormData.invoiceproducts[index];

                if (
                  !product.invoiceProductExtraLabels ||
                  !Array.isArray(product.invoiceProductExtraLabels)
                ) {
                  product.invoiceProductExtraLabels = [];
                }

                while (product.invoiceProductExtraLabels.length <= labelIndex) {
                  product.invoiceProductExtraLabels.push({
                    label: "",
                    value: "",
                    labelId: "",
                  });
                }

                product.invoiceProductExtraLabels[labelIndex] = {
                  label: label.valueLabel,
                  value: e.target.value,
                  labelId: label.id,
                };

                setFormData(updatedFormData);
              }}
              sx={{
                "& .MuiInputLabel-shrink": {
                  color: "#41c1ba !important",
                },
                "& fieldset": {
                  borderColor: "#41c1ba !important",
                  "& legend": {
                    "& span": {
                      color: "#41c1ba !important",
                    },
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#41c1ba",
                },
                width: "100%",
              }}
            />
          }
          </Box>
        )}

        {/* Actions: Save (✓) for new labels, Delete (✗) for all */}
        {(isEditing && isFirst) ? (
          <>
            <IconButton
              onClick={label.isNew ? saveLabel : updateLabel}
              color="success"
            >
              <CheckIcon />
            </IconButton>
            <IconButton onClick={deleteLabel} color="error">
              <CloseIcon sx={{ cursor: "pointer", fontSize: "1rem" }} />
            </IconButton>
          </>
        ) : null}
      </Grid>

      {/* Second Input */}
      {(isEditing && isFirst) && (
        <Grid item>
          <TextField
            size="small"
            disabled={isEditing ? true : false}
            sx={{
              "& .MuiInputLabel-shrink": {
                color: "#41c1ba !important",
              },
              "& fieldset": {
                borderColor: "#41c1ba !important",
                "& legend": {
                  "& span": {
                    color: "#41c1ba !important",
                  },
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#41c1ba",
              },
              width: "100%",
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ProductExtraLabels;
